import type {
  AccountEntity,
  AccountGroupEntity,
  AccountWithBalance,
  BudgetMonth,
  CategoryEntity,
  CategoryGroupEntity,
  PayeeEntity,
  PrefsMap,
  ReportData,
  ScheduleEntity,
  TransactionEntity,
} from './types';

/**
 * The real client talks to loot-core over `send(eventName, args)` (IPC in
 * Electron, HTTP+WS to the sync server in the browser). This clone keeps that
 * exact call shape and points it at `POST /ipc/:event`, which MSW answers from
 * src/mocks. Event names are reused verbatim where an upstream equivalent
 * exists so the call sites read like the original.
 */
export interface IpcEventMap {
  'accounts-get': { args: undefined; res: AccountWithBalance[] };
  'account-create': { args: Partial<AccountEntity>; res: string };
  'account-update': { args: AccountEntity; res: void };
  'account-close': { args: { id: string }; res: void };
  'account-reopen': { args: { id: string }; res: void };
  'account-move': {
    args: { id: string; targetId: string | null };
    res: void;
  };
  'account-groups-get': { args: undefined; res: AccountGroupEntity[] };

  'categories-get': { args: undefined; res: CategoryGroupEntity[] };
  'category-update': {
    args: Partial<CategoryEntity> & { id: string };
    res: void;
  };
  'category-create': {
    args: { name: string; category_group: string };
    res: string;
  };
  'category-group-create': { args: { name: string }; res: string };
  'category-group-update': {
    args: { id: string; name?: string; hidden?: 0 | 1 };
    res: void;
  };

  'payees-get': { args: undefined; res: PayeeEntity[] };
  'common-payees-get': {
    args: undefined;
    res: { id: string | null; name: string }[];
  };

  'transactions-get': {
    args: {
      accountIds?: string[] | null;
      startDate?: string | null;
      endDate?: string | null;
      includeDeleted?: boolean;
    };
    res: TransactionEntity[];
  };
  'transactions-batch-update': {
    args: {
      changes: { id: string; field: string; value: unknown }[];
      pref?: 'merge' | 'reorder';
    };
    res: { updated: TransactionEntity[]; deleted: string[] };
  };
  'transaction-delete': { args: { id: string }; res: { deleted: string[] } };
  'transaction-create': {
    args: Partial<TransactionEntity> & { account: string; date: string };
    res: TransactionEntity;
  };

  'envelope-budget-month': { args: { month: string }; res: BudgetMonth };
  'envelope-budget-set-month': {
    args: { month: string; category: string; amount: number };
    res: { applied: boolean };
  };
  'envelope-budget-cover': {
    args: { month: string };
    res: { covered: number };
  };
  'get-budget-bounds': { args: undefined; res: { min: string; max: string } };

  'schedules-get': { args: undefined; res: ScheduleEntity[] };
  'reports-get-data': { args: { dashboardId: string }; res: ReportData[] };

  'load-prefs': { args: undefined; res: PrefsMap };
  'save-prefs': { args: { diff: PrefsMap }; res: void };
  'load-global-prefs': { args: undefined; res: PrefsMap };
  'save-global-prefs': { args: { diff: PrefsMap }; res: void };

  'get-budgets': {
    args: undefined;
    res: { id: string; name: string; cloudFileId?: string | null }[];
  };
  'get-server-version': { args: undefined; res: string };
  'get-did-bootstrap': { args: undefined; res: boolean };
  'get-openid-config': { args: undefined; res: null };
  'server-changes-batch': { args: undefined; res: { changes: number } };
  undo: { args: undefined; res: { didUndo: boolean; messages: string[] } };
  redo: { args: undefined; res: { didRedo: boolean; messages: string[] } };
  'simplefin-status': { args: undefined; res: { enabled: boolean } };
  'gocardless-status': { args: undefined; res: { enabled: boolean } };
  'enablebanking-status': { args: undefined; res: { enabled: boolean } };
  'pluggyai-status': { args: undefined; res: { enabled: boolean } };
  'sync': { args: undefined; res: { syncs: never[] } };
}

export type IpcEvent = keyof IpcEventMap;

export async function send<E extends IpcEvent>(
  event: E,
  ...args: IpcEventMap[E]['args'] extends undefined
    ? []
    : [IpcEventMap[E]['args']]
): Promise<IpcEventMap[E]['res']> {
  const response = await fetch(`/ipc/${encodeURIComponent(event)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, args: args[0] ?? null }),
  });

  if (!response.ok) {
    throw new Error(`IPC ${event} failed with HTTP ${response.status}`);
  }

  const payload = (await response.json()) as {
    error?: string;
    data?: unknown;
  };

  if (payload.error) {
    throw new Error(`${event}: ${payload.error}`);
  }

  return payload.data as IpcEventMap[E]['res'];
}
